import { ChunkBuildRequest, ChunkGeometryData, buildChunk } from './ChunkBuilder';
import type { TerrainWorkerRequest, TerrainWorkerResponse } from './workers/terrain.worker';

interface Job {
  id: number;
  /** Planet-qualified identity; the key `byKey` is indexed by. */
  key: string;
  priority: number;
  request: ChunkBuildRequest;
  resolve: (data: ChunkGeometryData) => void;
  reject: (err: Error) => void;
  cancelled: boolean;
}

/**
 * Identity used to deduplicate and cancel a chunk build.
 *
 * A chunk key (`face:level:ix:iy`) is only unique *within* one body: `0:0:0:0`
 * names the same cube face on every planet. The pool is shared across bodies,
 * so deduplicating on the bare key lets one body's in-flight build satisfy a
 * different body's request for the same face, handing the second planet the
 * first planet's geometry — its radius, its relief and its palette baked into
 * the vertex colours. Those chunks then render displaced on (or near) the
 * wrong planet. The planet id makes the identity unique.
 */
export function poolJobKey(planetId: string, chunkKey: string): string {
  return `${planetId}/${chunkKey}`;
}

export interface WorkerPoolStats {
  workers: number;
  queued: number;
  inFlight: number;
  completed: number;
  cancelled: number;
  failed: number;
  /** Rolling average generation time, milliseconds. */
  averageMs: number;
}

/**
 * Pool of terrain generation workers.
 *
 * Design points that matter for a browser game:
 *
 *  - **One pool, not one worker per chunk.** Spawning a worker costs several
 *    milliseconds and a fresh module instantiation; at the rate chunks are
 *    requested during a descent that alone would stall the main thread.
 *
 *  - **Priority queue with cancellation.** The streamer re-prioritises every
 *    frame based on camera position and velocity. A chunk that has scrolled out
 *    of relevance is cancelled rather than generated and thrown away. During a
 *    fast atmospheric descent that discards the majority of queued work.
 *
 *  - **Transferables.** Geometry buffers are transferred, not structurally
 *    cloned, so a 300 KB chunk costs a pointer move instead of a copy.
 *
 *  - **Synchronous fallback.** When `Worker` is unavailable (tests, or a browser
 *    that refuses module workers) the pool builds on the calling thread. The
 *    game still runs; it just stutters. That is strictly better than failing to
 *    render a planet.
 */
export class WorkerPool {
  private workers: Worker[] = [];
  private idle: Worker[] = [];
  private queue: Job[] = [];
  private inFlight = new Map<Worker, Job>();
  private byKey = new Map<string, Job>();
  private nextJobId = 1;

  private completed = 0;
  private cancelledCount = 0;
  private failed = 0;
  private timingSum = 0;
  private timingCount = 0;
  private startTimes = new Map<number, number>();

  readonly supported: boolean;

  constructor(size: number) {
    this.supported = typeof Worker !== 'undefined';
    if (!this.supported) return;

    for (let i = 0; i < size; i++) {
      try {
        // Vite requires these options to be a static object literal so it can
        // resolve and bundle the worker at build time.
        const worker = new Worker(new URL('./workers/terrain.worker.ts', import.meta.url), {
          type: 'module',
          name: 'terrain-worker',
        });
        worker.onmessage = (e: MessageEvent<TerrainWorkerResponse>) => this.onMessage(worker, e.data);
        worker.onerror = (e) => this.onError(worker, e);
        this.workers.push(worker);
        this.idle.push(worker);
      } catch (err) {
        console.warn('[WorkerPool] failed to start a terrain worker; falling back to main thread', err);
        break;
      }
    }
  }

  get size(): number {
    return this.workers.length;
  }

  /**
   * Submits a chunk build. If the same planet-qualified key is already queued,
   * its priority is updated and the existing promise is returned rather than
   * duplicating work.
   */
  submit(request: ChunkBuildRequest, priority: number): Promise<ChunkGeometryData> {
    const jobKey = poolJobKey(request.params.planetId, request.key);
    const existing = this.byKey.get(jobKey);
    if (existing && !existing.cancelled) {
      existing.priority = priority;
      return new Promise((resolve, reject) => {
        const originalResolve = existing.resolve;
        const originalReject = existing.reject;
        existing.resolve = (d) => {
          originalResolve(d);
          resolve(d);
        };
        existing.reject = (e) => {
          originalReject(e);
          reject(e);
        };
      });
    }

    return new Promise<ChunkGeometryData>((resolve, reject) => {
      const job: Job = {
        id: this.nextJobId++,
        key: jobKey,
        priority,
        request,
        resolve,
        reject,
        cancelled: false,
      };
      this.byKey.set(job.key, job);

      if (this.workers.length === 0) {
        // Synchronous fallback. Deferred a macrotask so callers still observe
        // asynchronous behaviour and cannot accidentally depend on sync timing.
        setTimeout(() => {
          if (job.cancelled) {
            this.byKey.delete(job.key);
            this.cancelledCount++;
            return;
          }
          try {
            const data = buildChunk(request);
            this.byKey.delete(job.key);
            this.completed++;
            job.resolve(data);
          } catch (err) {
            this.byKey.delete(job.key);
            this.failed++;
            job.reject(err instanceof Error ? err : new Error(String(err)));
          }
        }, 0);
        return;
      }

      this.queue.push(job);
      this.pump();
    });
  }

  /** Cancels a queued or in-flight job. `jobKey` is the planet-qualified key from `poolJobKey`. */
  cancel(jobKey: string): void {
    const job = this.byKey.get(jobKey);
    if (!job) return;
    job.cancelled = true;

    const queueIndex = this.queue.indexOf(job);
    if (queueIndex >= 0) {
      this.queue.splice(queueIndex, 1);
      this.byKey.delete(jobKey);
      this.cancelledCount++;
      return;
    }

    // In flight: tell the worker, but it may already have finished.
    for (const [worker, active] of this.inFlight) {
      if (active === job) {
        const msg: TerrainWorkerRequest = { type: 'cancel', jobId: job.id };
        worker.postMessage(msg);
        break;
      }
    }
  }

  /** Updates the priority of a queued job. `jobKey` is the planet-qualified key from `poolJobKey`. */
  reprioritise(jobKey: string, priority: number): void {
    const job = this.byKey.get(jobKey);
    if (job) job.priority = priority;
  }

  isPending(jobKey: string): boolean {
    const job = this.byKey.get(jobKey);
    return job !== undefined && !job.cancelled;
  }

  private pump(): void {
    while (this.idle.length > 0 && this.queue.length > 0) {
      // Lowest priority value is served first.
      let bestIndex = 0;
      for (let i = 1; i < this.queue.length; i++) {
        if (this.queue[i].priority < this.queue[bestIndex].priority) bestIndex = i;
      }
      const job = this.queue.splice(bestIndex, 1)[0];
      if (job.cancelled) {
        this.byKey.delete(job.key);
        this.cancelledCount++;
        continue;
      }
      const worker = this.idle.pop()!;
      this.inFlight.set(worker, job);
      this.startTimes.set(job.id, performance.now());
      const msg: TerrainWorkerRequest = { type: 'build', jobId: job.id, request: job.request };
      worker.postMessage(msg);
    }
  }

  private onMessage(worker: Worker, response: TerrainWorkerResponse): void {
    const job = this.inFlight.get(worker);
    this.inFlight.delete(worker);
    this.idle.push(worker);

    const started = this.startTimes.get(response.jobId);
    if (started !== undefined) {
      this.timingSum += performance.now() - started;
      this.timingCount++;
      this.startTimes.delete(response.jobId);
    }

    if (job) this.byKey.delete(job.key);

    if (job && !job.cancelled) {
      if (response.type === 'done' && response.data) {
        this.completed++;
        job.resolve(response.data as ChunkGeometryData);
      } else if (response.type === 'cancelled') {
        this.cancelledCount++;
      } else {
        this.failed++;
        job.reject(new Error(response.message ?? 'Terrain worker failed'));
      }
    } else if (job) {
      this.cancelledCount++;
    }

    this.pump();
  }

  private onError(worker: Worker, event: ErrorEvent): void {
    console.error('[WorkerPool] worker error', event.message);
    const job = this.inFlight.get(worker);
    this.inFlight.delete(worker);
    this.idle.push(worker);
    if (job) {
      this.byKey.delete(job.key);
      this.failed++;
      job.reject(new Error(event.message));
    }
    this.pump();
  }

  stats(): WorkerPoolStats {
    return {
      workers: this.workers.length,
      queued: this.queue.length,
      inFlight: this.inFlight.size,
      completed: this.completed,
      cancelled: this.cancelledCount,
      failed: this.failed,
      averageMs: this.timingCount > 0 ? this.timingSum / this.timingCount : 0,
    };
  }

  dispose(): void {
    for (const w of this.workers) w.terminate();
    this.workers.length = 0;
    this.idle.length = 0;
    this.inFlight.clear();
    this.queue.length = 0;
    this.byKey.clear();
  }
}
